
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../LanguageContext';
import { CONTENT } from '../content';
import QRCodeBlock from '../components/QRCodeBlock';
import { supabase } from '../supabaseClient';

export default function Contact() {
  const { language } = useLanguage();
  const t = CONTENT[language].contact;
  const common = CONTENT[language].common;

  const [formState, setFormState] = useState({
    name: '',
    role: '',
    practice: '',
    email: '',
    phone: '',
    model: '',
    patients: ''
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // Reset dropdown defaults when language changes to ensure they match available options
  useEffect(() => {
    setFormState(prev => ({
        ...prev,
        role: t.roles[0],
        model: t.models[0]
    }));
  }, [language, t.roles, t.models]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      // 1. Send data to Supabase if configured
      if (!supabase) {
        throw new Error("Supabase client not configured");
      }

      const { error } = await supabase
        .from('inquiries')
        .insert([
          { 
            name: formState.name,
            role: formState.role,
            practice_name: formState.practice,
            email: formState.email,
            phone: formState.phone,
            current_model: formState.model,
            estimated_patients: formState.patients,
            status: 'Open', // Initial status for your tracker
            created_at: new Date().toISOString()
          }
        ]);

      if (error) {
        throw error;
      }
      
      // On success:
      setStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error("Error submitting form:", error);
      
      // Fallback: If Supabase keys aren't set up yet (client is null or connection failed),
      // we simulate success for the demo so the UI flow works.
      const isConfigError = (error as Error).message === "Supabase client not configured";
      
      // Safe check for process.env
      const isEnvMissing = typeof process === 'undefined' || !process.env || !process.env.REACT_APP_SUPABASE_URL;

      if (isConfigError || isEnvMissing) {
          console.warn("Supabase not configured. Simulating success for UI demo.");
          await new Promise(resolve => setTimeout(resolve, 1000));
          setStatus('success');
          window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
          setStatus('error');
      }
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-suncoast-offWhite min-h-screen py-20">
        <div className="container mx-auto px-4 max-w-2xl">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-suncoast-dark mb-4">
              {language === 'en' ? 'Request Received!' : '¡Solicitud Recibida!'}
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              {language === 'en' 
                ? "Thank you for sharing your practice details. A Suncoast MD director will analyze your potential fit and contact you shortly."
                : "Gracias por compartir los detalles de su práctica. Un director de Suncoast MD analizará su perfil y se pondrá en contacto con usted en breve."}
            </p>
            <button 
              onClick={() => setStatus('idle')}
              className="px-8 py-3 bg-suncoast-secondary text-white font-bold rounded hover:bg-suncoast-primary transition-colors"
            >
              {language === 'en' ? 'Return to Form' : 'Volver al Formulario'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-suncoast-offWhite min-h-screen py-16">
      <div className="container mx-auto px-4 lg:px-12">
        <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
          
          {/* Form Side */}
          <div className="md:w-1/2 p-8 md:p-12 relative">
            {status === 'submitting' && (
              <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
                 <div className="w-12 h-12 border-4 border-suncoast-accent border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            <h1 className="text-3xl font-bold text-suncoast-dark mb-4">{t.title}</h1>
            <p className="text-gray-600 mb-8">{t.subtitle}</p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">{t.labels.name}</label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 rounded p-3 focus:outline-none focus:border-suncoast-primary transition-colors"
                  value={formState.name}
                  onChange={e => setFormState({...formState, name: e.target.value})}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">{t.labels.role}</label>
                  <select 
                    className="w-full border border-gray-300 rounded p-3 focus:outline-none focus:border-suncoast-primary bg-white transition-colors"
                    value={formState.role}
                    onChange={e => setFormState({...formState, role: e.target.value})}
                  >
                    {t.roles.map((r, i) => <option key={i} value={r}>{r}</option>)}
                  </select>
                </div>
                 <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">{t.labels.model}</label>
                  <select 
                    className="w-full border border-gray-300 rounded p-3 focus:outline-none focus:border-suncoast-primary bg-white transition-colors"
                    value={formState.model}
                    onChange={e => setFormState({...formState, model: e.target.value})}
                  >
                    {t.models.map((m, i) => <option key={i} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">{t.labels.practice}</label>
                <input 
                  type="text" 
                  className="w-full border border-gray-300 rounded p-3 focus:outline-none focus:border-suncoast-primary transition-colors"
                  value={formState.practice}
                  onChange={e => setFormState({...formState, practice: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">{t.labels.email}</label>
                <input 
                  type="email" 
                  className="w-full border border-gray-300 rounded p-3 focus:outline-none focus:border-suncoast-primary transition-colors"
                  value={formState.email}
                  onChange={e => setFormState({...formState, email: e.target.value})}
                  required
                />
              </div>
              {/* Added Phone Input explicitly if not present previously */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
                <input 
                  type="tel" 
                  className="w-full border border-gray-300 rounded p-3 focus:outline-none focus:border-suncoast-primary transition-colors"
                  value={formState.phone}
                  onChange={e => setFormState({...formState, phone: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">{t.labels.patients}</label>
                <input 
                  type="number" 
                  className="w-full border border-gray-300 rounded p-3 focus:outline-none focus:border-suncoast-primary transition-colors"
                  value={formState.patients}
                  onChange={e => setFormState({...formState, patients: e.target.value})}
                />
              </div>

              <div className="pt-4">
                 <p className="text-xs text-gray-500 mb-4">{t.labels.privacy}</p>
                 <button 
                  type="submit" 
                  disabled={status === 'submitting'}
                  className="w-full bg-suncoast-accent text-white font-bold py-4 rounded hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0"
                 >
                   {status === 'submitting' ? (
                     <span className="flex items-center justify-center gap-2">
                       Processing...
                     </span>
                   ) : t.labels.submit}
                 </button>
              </div>
            </form>
          </div>

          {/* Calendar/Info Side */}
          <div className="md:w-1/2 bg-suncoast-primary text-white p-8 md:p-12 flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold mb-6">{t.scheduleDirectly}</h3>
              {/* Calendly Placeholder */}
              <div className="bg-white/10 border border-white/20 rounded-xl h-64 flex items-center justify-center mb-8 hover:bg-white/15 transition-colors cursor-pointer group">
                 <div className="text-center group-hover:scale-105 transition-transform">
                   <p className="font-bold text-lg mb-2">{t.calendar.placeholder}</p>
                   <p className="text-sm opacity-75">{t.calendar.desc}</p>
                 </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-6">
              <div>
                <h3 className="text-xl font-bold mb-4">{t.directContact}</h3>
                <p className="mb-2"><strong>{t.info.phone}:</strong> 561-388-1805</p>
                <p className="mb-2"><strong>{t.info.email}:</strong> info@suncoastmdpartners.com</p>
                <p><strong>{t.info.hq}:</strong> 1214 Lafayette St, Cape Coral, Florida 33904</p>
              </div>

              <div>
                 <QRCodeBlock />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
